import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { Input, Menu, MenuItem, Typeahead } from "react-bootstrap-typeahead";
import { injectIntl } from "react-intl";
import { FixtureDetailsContext } from "../../Config/FixtureDetails";
import { getOfficialRoles } from "../../Config/OfficialsRoles";
import GridTableDisplay from "../Base/Shared/GridTableDisplay";
import { uniqueBy } from "../Base/Utilities/CollectionUtilities";
import { sendFixtureRole } from "../Base/Utilities/ManagePlayers";
import TeamSetupCheckbox from "../TeamSetup/Team/TeamSetupCheckbox";

import "react-bootstrap-typeahead/css/Typeahead.scss";
import "./Officials.scss";

const formatRoleObj = (role) => {
  return {
    personId: role.personId,
    name: role.nameFullLocal || role.nameFullLatin,
  };
};

const Officials = ({ currentState, intl: { formatMessage } }) => {
  const { fixtureRoles, season, sport } = useContext(FixtureDetailsContext);
  const [officialsData, setOfficialsData] = useState([]);

  const fixtureEntity = currentState.mqtt?.entities.find((entity) => entity.entityId === "FIXTURE");

  const columns = useMemo(() => {
    return [
      formatMessage({ id: "person.playing", defaultMessage: "Active" }),
      formatMessage({ id: "person.role", defaultMessage: "Role" }),
      formatMessage({ id: "person.name", defaultMessage: "Name" }),
    ];
  }, [formatMessage]);

  const getAvailableOfficials = () => {
    const availableOfficials = [];

    if (fixtureRoles?.fixture?.length) {
      availableOfficials.push(...uniqueBy(fixtureRoles.fixture.map(formatRoleObj), "personId"));
    }

    if (fixtureRoles?.season?.length) {
      availableOfficials.push(...uniqueBy(fixtureRoles.season.map(formatRoleObj), "personId"));
    }

    return availableOfficials;
  };

  const availableOfficials = getAvailableOfficials();

  const getValidRoleNumberForPerson = (personData) => {
    const personsWithSameRole = officialsData.filter(
      (o) => o.role === personData.role && o.personId !== null && o.active,
    );
    const existingNumbers = personsWithSameRole.map((o) => o.number).sort((a, b) => a - b);
    const newPersonNumberGuess = existingNumbers.findIndex((o, index) => o !== index + 1);
    return newPersonNumberGuess !== -1 ? newPersonNumberGuess + 1 : personsWithSameRole.length + 1;
  };

  const officialsRoles = useMemo(
    () => [
      <option key="empty_role" value={""}></option>,
      Object.values(getOfficialRoles(sport)).map((entry) => (
        <option key={entry.role} value={entry.role}>
          {formatMessage({
            id: `role.${entry.role.toLowerCase()}`,
            defaultMessage: `${entry.role}`,
          })}
        </option>
      )),
    ],
    [formatMessage, sport],
  );

  useEffect(() => {
    const newOfficialsObj = officialsData.reduce((acc, o) => ({ ...acc, [`${o.personId}_${o.role}`]: o }), {});
    if (fixtureRoles?.fixture) {
      fixtureRoles.fixture.forEach(
        (official) =>
          (newOfficialsObj[`${official.personId}_${official.role}`] = {
            role: official.role,
            personId: official.personId,
            number: official.roleNumber,
            name: official.nameFullLocal || official.nameFullLatin,
            active: false,
          }),
      );
    }

    if (fixtureEntity?.roles) {
      Object.entries(fixtureEntity.roles).forEach(([role, persons]) => {
        Object.keys(persons).forEach((personId) => {
          newOfficialsObj[`${personId}_${role}`] = {
            role: role,
            personId: personId,
            number: persons[personId].number,
            name: persons[personId].name,
            active: true,
          };
        });
      });
    }
    setOfficialsData(Object.values(newOfficialsObj));
  }, [fixtureRoles, fixtureEntity]);

  const updateOfficialChangeUpstream = (officialToRemoveData, officialToAddData) => {
    sendFixtureRole(officialToRemoveData, currentState.mqtt, currentState.fixtureId, "deleted");
    // we have to delay sending add, because otherwise the responses come back in the wrong order and break logic here
    setTimeout(() => {
      sendFixtureRole(officialToAddData, currentState.mqtt, currentState.fixtureId, "added");
    }, 100);
  };

  const handleAddOfficials = () => {
    const newEmptyOfficial = { role: null, personId: null, active: false };
    const newOfficialsData = [...officialsData, newEmptyOfficial];
    setOfficialsData(newOfficialsData);
  };

  const handleOfficialRoleChanged = useCallback(
    (e, officialIndex) => {
      const oldOfficialPersonData = { ...officialsData[officialIndex] };
      const newOfficialPersonData = { ...officialsData[officialIndex] };
      // this means the role has been set to an empty one
      if (e.target.value === "") {
        if (oldOfficialPersonData.active) {
          sendFixtureRole(oldOfficialPersonData, currentState.mqtt, currentState.fixtureId, "deleted");
        }
        newOfficialPersonData.role = null;
        newOfficialPersonData.active = false;
      } else {
        newOfficialPersonData.role = e.target.value;
        if (newOfficialPersonData.active) {
          updateOfficialChangeUpstream(oldOfficialPersonData, newOfficialPersonData);
        }
      }
      const newOfficialsData = [...officialsData];
      newOfficialsData[officialIndex] = newOfficialPersonData;
      setOfficialsData(newOfficialsData);
    },
    [officialsData],
  );

  const handleOfficialPersonChange = useCallback(
    (selected, officialIndex) => {
      const oldOfficialPersonData = { ...officialsData[officialIndex] };
      const newOfficialPersonData = { ...officialsData[officialIndex] };
      // this means a person was unassigned from a official role
      if (selected.length === 0) {
        if (oldOfficialPersonData.active) {
          sendFixtureRole(oldOfficialPersonData, currentState.mqtt, currentState.fixtureId, "deleted");
        }
        newOfficialPersonData.personId = null;
        newOfficialPersonData.name = null;
        newOfficialPersonData.active = false;
      } else {
        newOfficialPersonData.personId = selected[0].personId;
        newOfficialPersonData.name = availableOfficials.find((o) => o.personId === selected[0].personId).name;
        if (newOfficialPersonData.active) {
          updateOfficialChangeUpstream(oldOfficialPersonData, newOfficialPersonData);
        }
      }
      const newOfficialsData = [...officialsData];
      newOfficialsData[officialIndex] = newOfficialPersonData;
      setOfficialsData(newOfficialsData);
    },
    [officialsData],
  );

  const handleOfficialActiveChange = useCallback(
    (e, officialIndex) => {
      const newOfficialPersonData = {
        ...officialsData[officialIndex],
      };
      newOfficialPersonData.active = e.target.checked;
      if (newOfficialPersonData.active) {
        newOfficialPersonData.number = getValidRoleNumberForPerson(newOfficialPersonData);
      }

      const newOfficialsData = [...officialsData];
      newOfficialsData[officialIndex] = newOfficialPersonData;
      setOfficialsData(newOfficialsData);

      const status = newOfficialPersonData.active ? "added" : "deleted";
      sendFixtureRole(newOfficialPersonData, currentState.mqtt, currentState.fixtureId, status);
    },
    [officialsData],
  );

  const rows = useMemo(() => {
    return officialsData.map((official, index) => {
      const isPersonSelected = official?.personId !== null;
      if (season.allowMatchOfficials) {
        return [
          <TeamSetupCheckbox
            checked={official.active}
            onChange={(e) => handleOfficialActiveChange(e, index)}
            enabled={official.personId !== null && official.role !== null}
          />,
          <select
            className={"officials--role-select"}
            value={official.role ?? ""}
            onChange={(e) => handleOfficialRoleChanged(e, index)}
          >
            {officialsRoles}
          </select>,
          <Typeahead
            id={`official-typeahead-${index}`}
            clearButton={isPersonSelected}
            options={availableOfficials}
            labelKey={(option) => option.name || ""}
            onChange={(selected) => handleOfficialPersonChange(selected, index)}
            selected={availableOfficials.filter((o) => o.personId === official.personId)}
            renderMenu={(results, menuProps) => (
              <Menu className="officials--person-menu" id={menuProps.id}>
                {results.map((result, index) => (
                  <MenuItem
                    option={result}
                    position={index}
                    className="officials--person-menu-item"
                    key={`officials-person-role-${index}`}
                  >
                    {result.name || ""}
                  </MenuItem>
                ))}
              </Menu>
            )}
            renderInput={({ inputRef, referenceElementRef, ...inputProps }) => (
              <Input
                {...inputProps}
                className={`officials--person-input officials--person-input-isEmpty-${!isPersonSelected}`}
                ref={(input) => {
                  inputRef(input);
                  referenceElementRef(input);
                }}
              />
            )}
          />,
        ];
      } else {
        return [
          <TeamSetupCheckbox
            checked={official.active}
            onChange={(e) => handleOfficialActiveChange(e, index)}
            enabled={official.personId !== null && official.role !== null}
          />,
          formatMessage({
            id: `role.${official.role.toLowerCase()}`,
            defaultMessage: `${official.role}`,
          }),
          official.name,
        ];
      }
    });
  }, [officialsData, availableOfficials, handleOfficialPersonChange, handleOfficialRoleChanged, officialsRoles]);

  return (
    <div className="game-details--game-officials">
      <div className="game-details--section-header-no-border">
        {formatMessage({
          id: "details.officials",
          defaultMessage: "Officials",
        })}
      </div>
      <div className="officials--container">
        <GridTableDisplay
          columns={columns}
          rows={rows}
          headerClass="officials--table-header"
          rowCellClass="officials-cell"
          tableKeyPrefix="officials-roster"
        />
        {season.allowMatchOfficials && (
          <div className={"officials--add-button"} onClick={handleAddOfficials}>
            <i className="fas fa-plus"></i>{" "}
            {formatMessage({
              id: "details.officials.addOfficials",
              defaultMessage: "Add Officials",
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default injectIntl(Officials);
